import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { ICompanySettings } from 'auth/types';
import { FormProvider, useNotificationContext } from 'core/states';
import { QuoteEntityType } from 'quotes/enums';
import { useQuoteActions } from 'quotes/hooks';
import { IQuoteVersionData } from 'quotes/types';

import { ChangeFreightForm } from './ChangeFreightForm';
import { changeFreightSchema } from './schema';
import { IChangeVersionDataForm } from './types';
import { getDefaultValues } from './utils';

interface IProps {
  quoteVersionData: IQuoteVersionData;
  partsTotal: number;
  freightSettings: Pick<ICompanySettings, 'defaultFreightPercentage' | 'minimumFreightCharge'>;
  isOpen: boolean;
  onClose: () => void;
  quoteEntityType?: QuoteEntityType;
  onSubmitSuccess?: () => void;
}

export const ChangeFreight = ({
  quoteVersionData,
  partsTotal,
  freightSettings,
  isOpen,
  onClose,
  quoteEntityType = QuoteEntityType.QUOTE,
  onSubmitSuccess,
}: IProps) => {
  const { clearNotification } = useNotificationContext();

  const { upsertQuoteVersionData } = useQuoteActions({ entityType: quoteEntityType });

  const defaultValues = useMemo(() => getDefaultValues(quoteVersionData), [quoteVersionData]);

  const onSubmit = () => async (data: IChangeVersionDataForm) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { freightMargin, freightPriceType, ...rest } = data;

    const { success } = await upsertQuoteVersionData({
      quoteVersionData: {
        ...quoteVersionData,
        ...rest,
        freightPercentage: data.freightPercentage / 100,
        freightGpm: freightMargin / 100,
      },
    });

    if (!success) {
      return;
    }

    onSubmitSuccess?.();

    setTimeout(() => {
      handleOnClose();
    }, 1000);
  };

  const handleOnClose = (reset?: UseFormReset<IChangeVersionDataForm>) => {
    reset && reset();
    onClose();
    clearNotification();
  };

  return (
    <FormProvider schema={changeFreightSchema()} defaultValues={defaultValues} resetDefaultValues>
      <ChangeFreightForm
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        partsTotal={partsTotal}
        freightSettings={freightSettings}
      />
    </FormProvider>
  );
};
